'use client'

import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import { BadgeProps } from '@mui/material'
import Paper from '@mui/material/Paper'
import MuiTab, { TabProps as MuiTabProps } from '@mui/material/Tab'
import { useTheme } from '@mui/material/styles'
import useMuiUseMediaQuery from '@mui/material/useMediaQuery'
import { ReactElement, ReactNode } from 'react'
import { tss } from 'tss-react/mui'
import InlineBadge from '../InlineBadge/InlineBadge'

const useStyles = tss
    .withName('Tabs')
    .withNestedSelectors<'isFirst' | 'isLast' | 'fullWidth'>()
    .create(({ theme, classes }) => ({
        paper: {
            borderRadius: theme.shape.borderRadius,

            [`&.${classes.fullWidth}.${classes.isFirst}`]: {
                borderBottomLeftRadius: 0,
            },
            [`&.${classes.fullWidth}.${classes.isLast}`]: {
                borderBottomRightRadius: 0,
            },
        },
        isFirst: {},
        isLast: {},
        fullWidth: {},
    }))

export type TabProps = Omit<MuiTabProps, 'label'> & {
    label: string
    count?: number
    countOptions?: Pick<BadgeProps, 'showZero' | 'max'>
}

const Tab = ({ label, value, count, countOptions, ...props }: TabProps) => (
    <MuiTab
        label={
            typeof count !== 'undefined' ? (
                <InlineBadge badgeContent={count} {...(countOptions ?? {})}>
                    {label}
                </InlineBadge>
            ) : (
                label
            )
        }
        value={value != null ? value : label}
        {...props}
        // Putting these down here (below the spreading props) ensures they have consistent values based on the names instead of the routes, especially for test snapshots
        aria-controls={label}
        id={label}
    />
)

export type TabsProps<TabItem> = {
    fullWidthAbove?: 'xs' | 'sm'
    tabs: {
        label: string
        value: string // MUI says this can be `any`, but in reality if you use `<TabContext>` (which we do) then tab switching won't work if this is not a string
        count?: number
        countOptions?: Pick<BadgeProps, 'showZero' | 'max'>
        content: ReactNode
    }[]
    currentTab: string
    onChangeTab: (newTab: TabItem) => void
}

function Tabs<TabItems>({
    fullWidthAbove = 'sm',
    currentTab = '',
    tabs,
    onChangeTab,
}: TabsProps<TabItems>): ReactElement | null {
    const theme = useTheme()
    const { classes, cx } = useStyles()
    const fullWidth = useMuiUseMediaQuery(theme.breakpoints.up(fullWidthAbove))
    const { tabItems, tabPanels } = tabs.reduce(
        (acc: { tabItems: ReactNode[]; tabPanels: ReactNode[] }, tab) => {
            const { content, label, value, count, countOptions } = tab

            acc.tabItems.push(
                <Tab
                    key={value}
                    label={label}
                    value={value}
                    count={count}
                    countOptions={countOptions}
                    aria-controls={label}
                    id={label}
                />
            )
            acc.tabPanels.push(
                <TabPanel key={value} value={value} aria-labelledby={label} id={label}>
                    {content}
                </TabPanel>
            )

            return acc
        },
        { tabItems: [], tabPanels: [] }
    )

    if (tabs.length < 1) {
        return null
    }

    const firstTab = tabs[0]
    const isFirst = currentTab === firstTab?.value
    const lastTab = tabs[tabs.length - 1]
    const isLast = currentTab === lastTab?.value

    return (
        <TabContext value={currentTab}>
            <Paper
                className={cx(
                    classes.paper,
                    isFirst && classes.isFirst,
                    isLast && classes.isLast,
                    fullWidth && classes.fullWidth
                )}
            >
                <TabList
                    onChange={(_, currentTabValue) => onChangeTab(currentTabValue)}
                    indicatorColor="primary"
                    variant={fullWidth ? 'fullWidth' : 'scrollable'}
                    scrollButtons
                    allowScrollButtonsMobile
                >
                    {tabItems}
                </TabList>
            </Paper>
            {tabPanels}
        </TabContext>
    )
}

export default Tabs
